"use strict";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result) __defProp(target, key, result);
  return result;
};
import { LitElement, html, nothing } from "lit";
import { state, property } from "lit/decorators.js";
import {
  loadStripe
} from "@stripe/stripe-js";
import { stcCSRFIO } from "../common/servers/csrf_server";
const stripeElementsConfig = [
  {
    type: "payment",
    id: "paymentElement"
  },
  {
    type: "linkAuthentication",
    id: "linkAuthenticationElement"
  },
  {
    type: "address",
    options: {
      mode: "billing",
      fields: {
        phone: "always"
      },
      validation: {
        phone: {
          required: "never"
        }
      }
    },
    id: "addressElement"
  }
];
export class StaStripePaymentForm extends LitElement {
  constructor() {
    super(...arguments);
    this.stripe = null;
    this.stripeElements = null;
    this.isLoading = false;
    this.paymentStatuses = {};
    this.currentPaymentType = null;
    this.isInitialLoad = true;
  }
  async firstUpdated() {
    this.stripe = await this.initializeStripeObject();
    if (!this.stripe) {
      return;
    }
    this.mountStripeElements(this.stripe, stripeElementsConfig);
    this.setupStripeElementsEventListeners();
    this.setupCoverFeesCheckboxListener();
    this.setupCouponCodeListener();
    this.checkForExistingCoupon();
  }
  createRenderRoot() {
    return this;
  }
  toggleStripeElement(type, action) {
    const element = this.stripeElements?.getElement(type);
    if (action === "mount") {
      const config = stripeElementsConfig.find((config2) => config2.type === type);
      if (config) {
        element?.mount(`#${config.id}`);
      }
    }
    if (action === "unmount") {
      element?.unmount();
    }
  }
  setupStripeElementsEventListeners() {
    this.stripeElements?.getElement("payment")?.on("change", (event) => {
      const newPaymentType = event.value.type;
      if (newPaymentType === "card") {
        this.toggleStripeElement("address", "mount");
        this.toggleStripeElement("linkAuthentication", "mount");
        if (this.currentPaymentType !== "card" && !this.isInitialLoad) {
          setTimeout(() => this.updateFeesForCard(), 100);
        }
        this.currentPaymentType = "card";
      } else {
        this.toggleStripeElement("address", "unmount");
        this.toggleStripeElement("linkAuthentication", "unmount");
      }
      if (newPaymentType === "us_bank_account") {
        if (this.currentPaymentType !== "us_bank_account" && !this.isInitialLoad) {
          setTimeout(() => this.updateFeesForACH(), 100);
        }
        this.currentPaymentType = "us_bank_account";
      }
      this.isInitialLoad = false;
    });
    this.stripeElements?.getElement("payment")?.on("ready", () => {
      const readyEvent = new CustomEvent("staStripeReady", { bubbles: true, composed: true });
      this.dispatchEvent(readyEvent);
    });
  }
  setupCoverFeesCheckboxListener() {
    const checkbox = document.getElementById("cover_processing_fees");
    if (!checkbox) return;
    checkbox.addEventListener("toggle-ended", () => {
      this.updateTotalForProcessingFees(checkbox.checked);
    });
  }
  setupCouponCodeListener() {
    const couponCodeButton = document.getElementById("applyCouponButton");
    if (!couponCodeButton) return;
    couponCodeButton.addEventListener("trigger-ended", () => {
      this.getAndApplyCouponCode();
    });
    const couponCodeBadge = document.getElementById("couponCodeBadge");
    if (!couponCodeBadge) return;
    couponCodeBadge.addEventListener("close-ended", () => {
      this.removeCouponCode();
    });
  }
  checkForExistingCoupon() {
    if (this.existingCouponCode) {
      this.getAndApplyCouponCode(this.existingCouponCode);
    }
  }
  parseApplePayRecurringConfig() {
    if (!this.applePayRecurringConfigJson) {
      return null;
    }
    try {
      return JSON.parse(this.applePayRecurringConfigJson);
    } catch {
      return null;
    }
  }
  buildPaymentElementOptions() {
    const options = {
      layout: { type: "tabs" }
    };
    const recurringConfig = this.parseApplePayRecurringConfig();
    if (!recurringConfig?.management_url) {
      return options;
    }
    const regularBilling = {
      amount: recurringConfig.amount_cents,
      label: recurringConfig.label,
      recurringPaymentIntervalUnit: recurringConfig.interval_unit,
      recurringPaymentIntervalCount: recurringConfig.interval_count
    };
    options.applePay = {
      recurringPaymentRequest: {
        paymentDescription: recurringConfig.payment_description,
        managementURL: recurringConfig.management_url,
        billingAgreement: recurringConfig.billing_agreement,
        regularBilling
      }
    };
    return options;
  }
  mountStripeElements(stripeObject, elementsConfig) {
    this.stripeElements = stripeObject.elements({
      clientSecret: this.clientSecret,
      appearance: {
        theme: "stripe",
        variables: {
          gridRowSpacing: "1rem"
        }
      }
    });
    elementsConfig.forEach((config) => {
      const elementOptions = config.type === "payment" ? this.buildPaymentElementOptions() : config.options || {};
      const element = this.stripeElements?.create(config.type, elementOptions);
      if (config.type !== "linkAuthentication" && config.type !== "address") {
        element?.mount(`#${config.id}`);
      }
    });
  }
  async initializeStripeObject() {
    const stripe = await loadStripe(this.publishableKey, { stripeAccount: this.stripeAccountId });
    if (!stripe) {
      return null;
    }
    return stripe;
  }
  async getAddressObject() {
    const addressElement = this.stripeElements?.getElement("address");
    const { complete, value } = await addressElement.getValue();
    if (!complete) {
      return;
    }
    return value;
  }
  async submitStripePaymentDetails(stripeObject) {
    if (!this.stripeElements) return void 0;
    const { error, paymentIntent } = await stripeObject.confirmPayment({
      elements: this.stripeElements,
      confirmParams: { return_url: `${window.location.origin}/stripe_testing` },
      redirect: "if_required"
    });
    if (error) {
      this.isLoading = false;
      this.unlockSubmitButton();
      if (error.type === "validation_error" || error.type === "invalid_request_error") {
        return void 0;
      }
      this.dispatchPaymentEvent(false, error.message || "Payment failed");
      return void 0;
    }
    this.paymentStatuses = { ...this.paymentStatuses, payment: true };
    return paymentIntent;
  }
  async storePaymentId(paymentIntent) {
    if (!paymentIntent || !["succeeded", "processing"].includes(paymentIntent.status)) {
      return void 0;
    }
    const hiddenField = document.getElementById(this.paymentFieldId);
    if (hiddenField) {
      hiddenField.value = paymentIntent.id;
    }
    this.paymentStatuses = { ...this.paymentStatuses, status: true };
    return true;
  }
  async submitStripePayment() {
    this.paymentStatuses = {};
    if (!this.stripe || !this.stripeElements) {
      this.dispatchPaymentEvent(false, "Stripe not initialized");
      return;
    }
    const couponBadge = document.getElementById("couponCodeBadge");
    const hasCouponCode = couponBadge && couponBadge.textContent && couponBadge.textContent.trim() !== "";
    if (hasCouponCode) {
      const couponIsValid = await this.validateCouponBeforeSubmit();
      if (!couponIsValid) {
        const couponCodeInput = document.getElementById("couponCodeInput");
        if (couponCodeInput && couponBadge && couponBadge.textContent) {
          couponCodeInput.value = couponBadge.textContent;
        }
        await this.getAndApplyCouponCode();
        const modal = document.getElementById("stripe-payment-dialog");
        if (modal) {
          modal.scrollTop = 0;
        }
        const couponInput = document.getElementById("couponCodeInput");
        if (couponInput) {
          setTimeout(() => {
            couponInput.focus();
          }, 100);
        }
        this.unlockSubmitButton();
        return;
      }
    }
    const totalAmountElement = document.getElementById("totalAmountValue");
    const totalAmount = parseFloat(totalAmountElement?.dataset.amount || "0");
    if (totalAmount === 0) {
      const hiddenField = document.getElementById(this.paymentFieldId);
      if (hiddenField) {
        hiddenField.value = "no_payment";
      }
      this.dispatchPaymentEvent(true, "Payment completed successfully");
      return;
    }
    this.isLoading = true;
    try {
      const paymentIntent = await this.submitStripePaymentDetails(this.stripe);
      if (!paymentIntent) {
        this.isLoading = false;
        this.unlockSubmitButton();
        return;
      }
      const paymentStatus = await this.storePaymentId(paymentIntent);
      if (paymentStatus) {
        this.dispatchPaymentEvent(true, "Payment completed successfully");
      } else {
        this.dispatchPaymentEvent(false, "Payment processing failed");
        this.unlockSubmitButton();
      }
    } catch (error) {
      this.dispatchPaymentEvent(false, error.message || "Payment failed");
      this.isLoading = false;
      this.unlockSubmitButton();
    }
    this.isLoading = false;
  }
  lockSubmitButton() {
    const submitButton = document.getElementById("submitPaymentDialog");
    if (submitButton) {
      submitButton.locked = true;
    }
  }
  unlockSubmitButton() {
    const submitButton = document.getElementById("submitPaymentDialog");
    if (submitButton) {
      submitButton.locked = false;
    }
  }
  dispatchPaymentEvent(success, message) {
    const event = new CustomEvent("paymentCompleted", {
      detail: { success, message },
      bubbles: true,
      composed: true
    });
    this.dispatchEvent(event);
  }
  async updateFeesForACH() {
    try {
      this.showLoadingIndicator();
      const paymentIntentId = this.clientSecret.split("_secret_")[0];
      const couponBadge = document.getElementById("couponCodeBadge");
      const couponCode = couponBadge?.textContent || "";
      const requestBody = {
        payment_intent_id: paymentIntentId,
        object_type: this.objectType,
        object_id: this.objectId,
        coupon_code: couponCode
      };
      if (this.feeId) {
        requestBody.fee_id = this.feeId;
      }
      const response = await stcCSRFIO.invoke(`/stripe_update_fees_for_ach/${this.objectId}`, {
        method: "PATCH",
        body: JSON.stringify(requestBody)
      });
      if (response && response.data && typeof response.data === "object" && response.data !== null) {
        const data = response.data;
        this.updateModalAmounts(parseFloat(data.new_amount), parseFloat(data.new_fee), parseFloat(data.coupon_total), couponCode);
        await this.refreshPaymentElementAfterAmountUpdate();
      }
    } catch (error) {
    } finally {
      this.hideLoadingIndicator();
    }
  }
  async updateFeesForCard() {
    try {
      this.showLoadingIndicator();
      const paymentIntentId = this.clientSecret.split("_secret_")[0];
      const couponBadge = document.getElementById("couponCodeBadge");
      const couponCode = couponBadge?.textContent || "";
      const requestBody = {
        payment_intent_id: paymentIntentId,
        object_type: this.objectType,
        object_id: this.objectId,
        coupon_code: couponCode
      };
      if (this.feeId) {
        requestBody.fee_id = this.feeId;
      }
      const response = await stcCSRFIO.invoke(`/stripe_update_fees_for_card/${this.objectId}`, {
        method: "PATCH",
        body: JSON.stringify(requestBody)
      });
      if (response && response.data && typeof response.data === "object" && response.data !== null) {
        const data = response.data;
        this.updateModalAmounts(parseFloat(data.new_amount), parseFloat(data.new_fee), parseFloat(data.coupon_total), couponCode);
        await this.refreshPaymentElementAfterAmountUpdate();
      }
    } catch (error) {
    } finally {
      this.hideLoadingIndicator();
    }
  }
  async updateTotalForProcessingFees(shouldShowFees) {
    try {
      this.showLoadingIndicator();
      const paymentIntentId = this.clientSecret.split("_secret_")[0];
      const paymentMethod = this.currentPaymentType === "us_bank_account" ? "us_bank_account" : "card";
      const couponBadge = document.getElementById("couponCodeBadge");
      const couponCode = couponBadge?.textContent || "";
      const requestBody = {
        payment_intent_id: paymentIntentId,
        object_type: this.objectType,
        object_id: this.objectId,
        should_show_fees: shouldShowFees.toString(),
        payment_method: paymentMethod,
        coupon_code: couponCode
      };
      if (this.feeId) {
        requestBody.fee_id = this.feeId;
      }
      const response = await stcCSRFIO.invoke(`/stripe_update_total_for_processing_fees/${this.objectId}`, {
        method: "PATCH",
        body: JSON.stringify(requestBody)
      });
      if (response && response.data && typeof response.data === "object" && response.data !== null) {
        const data = response.data;
        this.updateModalAmounts(parseFloat(data.new_amount), parseFloat(data.new_fee), parseFloat(data.coupon_total), couponCode);
        this.toggleProcessingFeeRow(shouldShowFees);
        await this.refreshPaymentElementAfterAmountUpdate();
      }
    } catch (error) {
    } finally {
      this.hideLoadingIndicator();
    }
  }
  async validateCouponBeforeSubmit() {
    try {
      const couponBadge = document.getElementById("couponCodeBadge");
      const couponCode = couponBadge?.textContent?.trim() || "";
      let url = `/stripe_apply_coupons/${this.objectId}?object_type=${encodeURIComponent(this.objectType)}&object_id=${encodeURIComponent(this.objectId.toString())}&coupon_code=${encodeURIComponent(couponCode)}`;
      if (this.feeId) {
        url += `&fee_id=${encodeURIComponent(this.feeId)}`;
      }
      const response = await stcCSRFIO.invoke(url, {
        method: "GET"
      });
      if (response && response.data && typeof response.data === "object" && response.data !== null && !Array.isArray(response.data)) {
        const data = response.data;
        return data.coupon_valid === true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }
  async getAndApplyCouponCode(couponCodeParam) {
    try {
      this.showLoadingIndicator();
      const paymentIntentId = this.clientSecret.split("_secret_")[0];
      const paymentMethod = this.currentPaymentType === "us_bank_account" ? "us_bank_account" : "card";
      const couponCode = couponCodeParam || document.getElementById("couponCodeInput").value;
      const requestBody = {
        payment_intent_id: paymentIntentId,
        object_type: this.objectType,
        object_id: this.objectId,
        payment_method: paymentMethod,
        coupon_code: couponCode
      };
      if (this.feeId) {
        requestBody.fee_id = this.feeId;
      }
      const response = await stcCSRFIO.invoke(`/stripe_apply_coupons/${this.objectId}`, {
        method: "PATCH",
        body: JSON.stringify(requestBody)
      });
      if (response && response.data && typeof response.data === "object" && response.data !== null) {
        const data = response.data;
        const newAmount = parseFloat(data.new_amount);
        this.updateModalAmounts(newAmount, parseFloat(data.new_fee), parseFloat(data.coupon_total), couponCode);
        const couponBadge = document.getElementById("couponCodeBadge");
        const couponBadgeContainer = document.getElementById("couponCodeBadgeContainer");
        const validationMessage = document.getElementById("couponCodeValidationNotice");
        if (data.coupon_valid === true) {
          if (couponBadge) {
            couponBadge.textContent = couponCode;
          }
          if (couponBadgeContainer) {
            couponBadgeContainer.classList.remove("hide");
          }
          if (validationMessage) {
            validationMessage.value = "";
            validationMessage.hidden = true;
          }
          this.syncCheckoutVisibilityForAmount(newAmount);
        } else {
          if (couponBadgeContainer) {
            couponBadgeContainer.classList.add("hide");
          }
          if (couponBadge) {
            couponBadge.textContent = "";
          }
          if (validationMessage) {
            validationMessage.value = data.invalid_message;
            validationMessage.hidden = false;
          }
          this.syncCheckoutVisibilityForAmount(newAmount);
        }
        await this.refreshPaymentElementAfterAmountUpdate();
      }
    } catch (error) {
    } finally {
      this.hideLoadingIndicator();
    }
  }
  async clearCouponOnCancel() {
    const couponBadge = document.getElementById("couponCodeBadge");
    const hasCouponCode = couponBadge && couponBadge.textContent && couponBadge.textContent.trim() !== "";
    if (!hasCouponCode) return;
    await this.removeCouponCode();
  }
  async removeCouponCode() {
    try {
      this.showLoadingIndicator();
      const paymentIntentId = this.clientSecret.split("_secret_")[0];
      const paymentMethod = this.currentPaymentType === "us_bank_account" ? "us_bank_account" : "card";
      const requestBody = {
        payment_intent_id: paymentIntentId,
        object_type: this.objectType,
        object_id: this.objectId,
        payment_method: paymentMethod,
        coupon_code: ""
      };
      if (this.feeId) {
        requestBody.fee_id = this.feeId;
      }
      const response = await stcCSRFIO.invoke(`/stripe_apply_coupons/${this.objectId}`, {
        method: "PATCH",
        body: JSON.stringify(requestBody)
      });
      if (response && response.data && typeof response.data === "object" && response.data !== null) {
        const data = response.data;
        const newAmount = parseFloat(data.new_amount);
        this.updateModalAmounts(newAmount, parseFloat(data.new_fee), 0);
        const couponBadge = document.getElementById("couponCodeBadge");
        const couponBadgeContainer = document.getElementById("couponCodeBadgeContainer");
        if (couponBadge) {
          couponBadge.textContent = "";
        }
        if (couponBadgeContainer) {
          couponBadgeContainer.classList.add("hide");
        }
        this.syncCheckoutVisibilityForAmount(newAmount);
        await this.refreshPaymentElementAfterAmountUpdate();
      }
    } catch (error) {
    } finally {
      this.hideLoadingIndicator();
    }
  }
  toggleProcessingFeeRow(shouldShow) {
    const processingFeeRow = document.getElementById("processingFeeRow");
    if (processingFeeRow) {
      if (shouldShow) {
        processingFeeRow.classList.remove("hide");
      } else {
        processingFeeRow.classList.add("hide");
      }
    }
  }
  syncCheckoutVisibilityForAmount(newAmount) {
    if (newAmount < 0.5) {
      this.hideStripePaymentElements();
    } else {
      this.showStripePaymentElements();
    }
  }
  hideStripePaymentElements() {
    const checkoutHeading = Array.from(document.querySelectorAll("h3")).find((h3) => h3.textContent?.trim() === "Checkout");
    if (checkoutHeading) {
      checkoutHeading.style.display = "none";
    }
    const stripeContainer = document.getElementById("modalStripePaymentContainer");
    if (stripeContainer) {
      stripeContainer.style.display = "none";
    }
  }
  showStripePaymentElements() {
    const checkoutHeading = Array.from(document.querySelectorAll("h3")).find((h3) => h3.textContent?.trim() === "Checkout");
    if (checkoutHeading) {
      checkoutHeading.style.display = "";
    }
    const stripeContainer = document.getElementById("modalStripePaymentContainer");
    if (stripeContainer) {
      stripeContainer.style.display = "";
    }
  }
  async refreshPaymentElementAfterAmountUpdate() {
    if (!this.stripeElements) {
      return;
    }
    try {
      await this.stripeElements.fetchUpdates();
    } catch (error) {
    }
  }
  updateModalAmounts(newAmount, newFee, newCouponTotal, couponCode) {
    const displayAmount = newAmount < 0.5 ? 0 : newAmount;
    const totalAmountElement = document.getElementById("totalAmountValue");
    if (totalAmountElement) {
      totalAmountElement.textContent = `$${displayAmount.toFixed(2)}`;
      totalAmountElement.dataset.amount = displayAmount.toString();
    }
    const shouldShowProcessingFeeCheckbox = displayAmount > 0;
    const shouldShowProcessingFeeRow = displayAmount > 0 && newFee > 0;
    this.toggleProcessingFeeCheckbox(shouldShowProcessingFeeCheckbox);
    this.toggleProcessingFeeRow(shouldShowProcessingFeeRow);
    const processingFeeElement = document.getElementById("processingFeeAmount");
    if (processingFeeElement) {
      processingFeeElement.textContent = `$${newFee.toFixed(2)}`;
    }
    const couponCodeInputElement = document.getElementById("couponCodeInput");
    const couponDiscountRow = document.getElementById("couponDiscountRow");
    if (newCouponTotal !== 0) {
      const couponBadge = document.getElementById("couponCodeBadge");
      const enteredCouponCode = couponCode || couponBadge?.textContent || couponCodeInputElement?.value || "";
      if (couponDiscountRow) {
        couponDiscountRow.classList.remove("hide");
      }
      const couponDiscountTitle = document.getElementById("couponDiscountTitle");
      if (couponDiscountTitle) {
        couponDiscountTitle.textContent = `Coupon Code: ${enteredCouponCode}`;
      }
      const couponDiscountAmount = document.getElementById("couponDiscountAmount");
      if (couponDiscountAmount) {
        couponDiscountAmount.textContent = `-$${newCouponTotal.toFixed(2)}`;
      }
    } else if (couponDiscountRow) {
      couponDiscountRow.classList.add("hide");
    }
    if (couponCodeInputElement) {
      couponCodeInputElement.value = "";
    }
  }
  toggleProcessingFeeCheckbox(shouldShow) {
    const container = document.getElementById("processingFeeCheckboxContainer");
    if (!container) return;
    if (shouldShow) {
      container.classList.remove("hide");
    } else {
      container.classList.add("hide");
    }
  }
  showLoadingIndicator() {
    const modal = document.getElementById("stripe-payment-dialog");
    if (modal) {
      const indicator = document.createElement("hi-indicator");
      indicator.id = "fee-update-loading";
      indicator.style.position = "absolute";
      indicator.style.top = "0";
      indicator.style.left = "0";
      indicator.style.width = "100%";
      indicator.style.height = "100%";
      indicator.style.backgroundColor = "rgba(255, 255, 255, 0.8)";
      indicator.style.zIndex = "9999";
      indicator.style.display = "flex";
      indicator.style.alignItems = "center";
      indicator.style.justifyContent = "center";
      modal.appendChild(indicator);
    }
  }
  hideLoadingIndicator() {
    const indicator = document.getElementById("fee-update-loading");
    if (indicator) {
      indicator.remove();
    }
  }
  renderPaymentStatusAlert() {
    if (!this.paymentStatuses.status) {
      return nothing;
    }
  }
  renderElementAnchors() {
    return html`
      ${stripeElementsConfig.map((config) => {
      return html`<div id="${config.id}" class="stripe-element mb-8"></div>`;
    })}
    `;
  }
  render() {
    return html`
      <div class="width-100">
        ${this.renderPaymentStatusAlert()}
        <div id="stripe-payment-form">
          ${this.renderElementAnchors()}
        </div>
      </div>
    `;
  }
}
__decorateClass([
  property({ type: String, attribute: "publishable-key" })
], StaStripePaymentForm.prototype, "publishableKey", 2);
__decorateClass([
  property({ type: String, attribute: "client-secret" })
], StaStripePaymentForm.prototype, "clientSecret", 2);
__decorateClass([
  property({ type: String, attribute: "stripe-account-id" })
], StaStripePaymentForm.prototype, "stripeAccountId", 2);
__decorateClass([
  property({ type: String, attribute: "payment-field-id" })
], StaStripePaymentForm.prototype, "paymentFieldId", 2);
__decorateClass([
  property({ type: String, attribute: "object-type" })
], StaStripePaymentForm.prototype, "objectType", 2);
__decorateClass([
  property({ type: String, attribute: "object-id" })
], StaStripePaymentForm.prototype, "objectId", 2);
__decorateClass([
  property({ type: String, attribute: "existing-coupon-code" })
], StaStripePaymentForm.prototype, "existingCouponCode", 2);
__decorateClass([
  property({ type: String, attribute: "fee-id" })
], StaStripePaymentForm.prototype, "feeId", 2);
__decorateClass([
  property({ type: String, attribute: "apple-pay-recurring-config" })
], StaStripePaymentForm.prototype, "applePayRecurringConfigJson", 2);
__decorateClass([
  state()
], StaStripePaymentForm.prototype, "stripe", 2);
__decorateClass([
  state()
], StaStripePaymentForm.prototype, "stripeElements", 2);
__decorateClass([
  state()
], StaStripePaymentForm.prototype, "isLoading", 2);
__decorateClass([
  state()
], StaStripePaymentForm.prototype, "paymentStatuses", 2);
__decorateClass([
  state()
], StaStripePaymentForm.prototype, "currentPaymentType", 2);
customElements.define("sta-stripe-payment-form", StaStripePaymentForm);
